import { FormControl, FormLabel, Text, FormErrorMessage, Box, useToast } from "@chakra-ui/react";
import { CreatableSelect } from "chakra-react-select";
import { useState } from "react";
import { SelectOption } from "../BaseSelect";
import api from "../../../api";
import { useAppDispatch } from "../../../store";
import { fetchGuides } from "../../../store/slices/guides";
import { DictionaryType } from "../../../model/dictionaries";

interface CreatableProfileSelectProps {
  fieldTitle?: string;
  requiredField?: boolean;
  options: SelectOption[];
  placeholder?: string;
  name?: string;
  value?: any;
  onChange?: (value: any) => void;
  errorText?: string;
  isEmpty?: boolean;
  containerStyles?: React.CSSProperties;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
  isSearchable?: boolean;
  guidesToRefetch?: DictionaryType[];
}

export const CreatableProfileSelect = ({
  fieldTitle,
  requiredField = false,
  options,
  placeholder = "Выберите опцию",
  name,
  value,
  onChange,
  errorText,
  isEmpty,
  containerStyles,
  isDisabled = false,
  size = "md",
  isSearchable = true,
  guidesToRefetch = [
    "Grade",
    "Country",
    "JobFormat",
    "JobWorkload",
    "AttractionPeriod",
    "ApplicationStatus",
    "VacancyStatus",
    "Skill",
    "CandidateSource",
    "Town",
    "BusinessArea",
    "ProjectStatus",
    "Sex",
    "Currency",
    "LegalEntity",
    "ProjectFormat",
    "ApplicationProfile",
    "CandidateStatus",
  ],
  ...selectProps
}: CreatableProfileSelectProps) => {
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const hasError = !!errorText;

  const selectedOption = options.find((option) => option.value == value);

  const handleCreateProfile = async (profileName: string): Promise<void> => {
    if (!profileName.trim()) return;

    setIsCreating(true);
    try {
      const response = await api.guides.createProfileDictionary({ name: profileName.trim() });
      
      if (response.data && response.data.id) {
        const newProfileId = response.data.id;

        // Обновляем список справочников и ждем завершения
        await dispatch(fetchGuides(guidesToRefetch)).unwrap();

        toast({
          title: "Профиль создан",
          description: `Профиль "${profileName}" успешно создан`,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });

        // Устанавливаем значение после обновления справочников
        onChange?.(newProfileId);
      }
    } catch (error: any) {
      toast({
        title: "Ошибка при создании профиля",
        description: error?.response?.data?.message || "Не удалось создать профиль",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const NoOptionsMessage = ({ inputValue }: { inputValue: string }) => {
    return (
      <Box p={3} textAlign="center">
        <Text fontSize="sm" color="gray.600">
          {inputValue ? `Профиль "${inputValue}" не найден. Нажмите Enter для создания.` : "Нет доступных опций"}
        </Text>
      </Box>
    );
  };

  return (
    <FormControl isInvalid={hasError} style={containerStyles}>
      {fieldTitle && (
        <FormLabel fontSize="sm" fontWeight="medium" color="gray.700" display="flex" alignItems="center" gap={1} mb={2}>
          {fieldTitle}
          {requiredField && (
            <Text as="span" color="red">
              *
            </Text>
          )}
        </FormLabel>
      )}
      <CreatableSelect
        name={name}
        value={selectedOption as any}
        onChange={(selected: any) => {
          onChange?.((selected as SelectOption | null)?.value || null);
        }}
        onCreateOption={handleCreateProfile}
        options={options}
        placeholder={placeholder}
        isDisabled={isDisabled || isCreating}
        isSearchable={isSearchable}
        isClearable={true}
        isLoading={isCreating}
        formatCreateLabel={(inputValue: string) => `Создать "${inputValue}"`}
        size={size}
        components={{
          NoOptionsMessage: (props) => <NoOptionsMessage inputValue={props.selectProps.inputValue} />,
        }}
        chakraStyles={{
          control: (provided) => ({
            ...provided,
            borderColor: hasError ? "red.500" : "gray.200",
            _hover: {
              borderColor: hasError ? "red.500" : "blue.400",
            },
            _focus: {
              borderColor: hasError ? "red.500" : "blue.500",
              boxShadow: hasError ? "0 0 0 1px red.500" : "0 0 0 2px rgba(66, 153, 225, 0.2)",
            },
          }),
          placeholder: (provided) => ({
            ...provided,
            color: "gray.400",
            fontSize: "sm",
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: "sm",
            color: "gray.700",
          }),
          dropdownIndicator: (provided) => ({
            ...provided,
            backgroundColor: "transparent",
            color: "gray.500",
            _hover: {
              backgroundColor: "transparent",
              color: "gray.700",
            },
          }),
          indicatorSeparator: (provided) => ({
            ...provided,
            backgroundColor: "gray.200",
          }),
          option: (provided, state) => ({
            ...provided,
            fontSize: "sm",
            backgroundColor: state.isSelected ? "blue.500" : state.isFocused ? "blue.50" : "white",
            color: state.isSelected ? "white" : "gray.700",
            _hover: {
              backgroundColor: state.isSelected ? "blue.500" : "blue.50",
            },
          }),
        }}
        {...selectProps}
      />
      {errorText && <FormErrorMessage fontSize="xs">{errorText}</FormErrorMessage>}
    </FormControl>
  );
};

